import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Send, ChevronDown, ChevronUp, Loader2, Search, X,
  MoreVertical, Package, Pencil, Copy, Trash2,
} from 'lucide-react';
import {
  sendPacket,
  renamePacket,
  duplicatePacket,
  deletePacket,
  type PacketSummary,
  type PacketCandidate,
} from '../../services/api';
import { formatDate } from './constants';
import { useAdminData } from './AdminDataContext';
import { useTenant } from '../../context/TenantContext';
import { BatchDetailPage } from './BatchDetailPage';

function FilterDropdown({ options, selected, onChange }: { options: string[]; selected: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const dropRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [open]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt];
    onChange(next);
  };

  return (
    <span className="inline-block ml-1">
      <button ref={btnRef} onClick={() => setOpen(!open)} className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${selected.length > 0 ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600 hover:text-gray-500'}`}>
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" /></svg>
      </button>
      {open && ReactDOM.createPortal(
        <div ref={dropRef} className="fixed w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto font-normal text-left text-sm" style={{ top: pos.top, left: pos.left, zIndex: 9999 }}>
          {selected.length > 0 && (
            <button onClick={() => onChange([])} className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800">Clear</button>
          )}
          {options.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <button key={opt} onClick={() => toggle(opt)} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 ${checked ? 'text-blue-600 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'}`}>
                  {checked && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                </div>
                {opt.charAt(0).toUpperCase() + opt.slice(1)}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </span>
  );
}

function SortHeader({ label, sortKey, currentSort, onSort, filterOptions, filterValue, onFilter, className }: {
  label: string; sortKey: string;
  currentSort: { col: string; dir: 'asc' | 'desc' } | null;
  onSort: (col: string) => void;
  filterOptions?: string[];
  filterValue?: string[];
  onFilter?: (v: string[]) => void;
  className?: string;
}) {
  const isActive = currentSort?.col === sortKey;
  return (
    <th className={`px-4 py-3 font-semibold select-none ${className || ''}`}>
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-1.5 cursor-pointer hover:text-gray-900 dark:hover:text-gray-100" onClick={() => onSort(sortKey)}>
          {label}
          <div className="flex flex-col">
            <ChevronUp className={`w-2 h-2 -mb-0.5 ${isActive && currentSort?.dir === 'asc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
            <ChevronDown className={`w-2 h-2 ${isActive && currentSort?.dir === 'desc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
          </div>
        </div>
        {filterOptions && onFilter && (
          <FilterDropdown options={filterOptions} selected={filterValue ?? []} onChange={onFilter} />
        )}
      </div>
    </th>
  );
}

function SendBatchModal({ packet, onClose, onDone }: { packet: PacketSummary; onClose: () => void; onDone: () => void }) {
  const { tenants } = useTenant();
  const [selectedTenantIds, setSelectedTenantIds] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);

  const toggleTenant = (id: number) => {
    setSelectedTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const names: Record<number, string> = {};
      tenants.forEach((t) => { if (selectedTenantIds.has(t.id)) names[t.id] = t.name || t.domain; });
      await sendPacket(packet.id, Array.from(selectedTenantIds), names);
      onDone();
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-full overflow-y-auto animate-in zoom-in-95 duration-200" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between sticky top-0 bg-white dark:bg-gray-900 z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send Batch</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {packet.candidatesCount} candidates from <span className="font-medium text-gray-700 dark:text-gray-300">{packet.name}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Companies<span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="space-y-2">
              {tenants.length === 0 && (
                <div className="text-center py-4">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin mx-auto" />
                </div>
              )}
              {tenants.map((tenant) => (
                <div key={tenant.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedTenantIds.has(tenant.id)}
                      onChange={() => toggleTenant(tenant.id)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{tenant.name || tenant.domain}</span>
                      {tenant.name && tenant.domain && (
                        <span className="text-xs text-gray-400 ml-2">{tenant.domain}</span>
                      )}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || selectedTenantIds.size === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Batch'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function BatchMenu({ packet, onRename, onDuplicate, onDelete }: {
  packet: PacketSummary; onRename: () => void; onDuplicate: () => void; onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 160 });
    }
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={(ev) => { ev.stopPropagation(); setOpen(!open); }}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-300 transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && ReactDOM.createPortal(
        <div ref={menuRef} className="fixed w-auto min-w-[10rem] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 text-sm z-[9999]" style={{ top: pos.top, left: pos.left }}>
          <button onClick={() => { setOpen(false); onRename(); }} className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300">
            <Pencil className="w-3.5 h-3.5" />Rename batch
          </button>
          <button onClick={() => { setOpen(false); onDuplicate(); }} className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300">
            <Copy className="w-3.5 h-3.5" />Duplicate batch
          </button>
          <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
          <button onClick={() => { setOpen(false); onDelete(); }} className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-red-600 dark:text-red-400">
            <Trash2 className="w-3.5 h-3.5" />Delete batch
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

function RenameModal({ packet, onClose, onDone }: { packet: PacketSummary; onClose: () => void; onDone: (name: string) => void }) {
  const [name, setName] = useState(packet.name);

  const handleSave = () => {
    if (!name.trim() || name.trim() === packet.name) { onClose(); return; }
    onDone(name.trim());
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Rename Batch</h3>
        </div>
        <div className="p-6">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={!name.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DeleteBatchModal({ packet, onClose, onConfirm, deleting }: {
  packet: PacketSummary; onClose: () => void; onConfirm: () => void; deleting: boolean;
}) {
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Batch</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-gray-100">{packet.name}</span>? This will remove the batch and unlink all {packet.candidatesCount} candidate(s). Candidates will remain in the pool.
          </p>
        </div>
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={deleting} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function BatchesSection() {
  const { packets: contextPackets, refreshPackets: contextRefreshPackets, loading: contextLoading } = useAdminData();
  const [packets, setPackets] = useState<PacketSummary[]>(contextPackets);
  const [refreshing, setRefreshing] = useState(false);
  const loading = contextLoading || refreshing;
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [sendPacketTarget, setSendPacketTarget] = useState<PacketSummary | null>(null);
  const [sort, setSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const [renameTarget, setRenameTarget] = useState<PacketSummary | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PacketSummary | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const [copyingBatchId, setCopyingBatchId] = useState<number | null>(null);

  // Cache expanded candidate data to support name search across all batches
  const candidateCache = useRef<Map<number, PacketCandidate[]>>(new Map());

  useEffect(() => { setPackets(contextPackets); }, [contextPackets]);

  const handleRemoveFromDetail = (packetId: number, removedCount: number) => {
    setPackets((prev) =>
      prev.map((p) => p.id === packetId ? { ...p, candidatesCount: Math.max(0, p.candidatesCount - removedCount) } : p),
    );
    candidateCache.current.delete(packetId);
  };

  const refreshPackets = async () => {
    setRefreshing(true);
    await contextRefreshPackets();
    setRefreshing(false);
  };

  const handleDuplicate = async (packet: PacketSummary) => {
    setDuplicatingId(packet.id);
    try {
      const result = await duplicatePacket(packet.id);
      setDuplicatingId(null);
      setCopyingBatchId(result.id);
      setPackets((prev) => [{
        id: result.id,
        name: result.name,
        company: null,
        status: 'draft' as const,
        candidatesCount: result.candidatesCopied,
        createdAt: new Date().toISOString(),
        sentAt: null,
        openedAt: null,
        createdBy: null,
        sentBy: null,
      }, ...prev]);
      setTimeout(() => setCopyingBatchId(null), 3000);
    } catch { /* silent */ }
    finally { setDuplicatingId(null); }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    setDeletingId(targetId);
    setDeleteTarget(null);
    try {
      await deletePacket(targetId);
      candidateCache.current.delete(targetId);
      setPackets((prev) => prev.filter((p) => p.id !== targetId));
      if (selectedBatchId === targetId) setSelectedBatchId(null);
    } catch {
      setDeletingId(null);
    }
  };

  const customerNameMap = React.useMemo(() => {
    const map = new Map<number, string | null>();
    const sent = packets.filter((p) => p.sentAt && p.company).sort((a, b) => new Date(a.sentAt!).getTime() - new Date(b.sentAt!).getTime());
    const companyDayCounts = new Map<string, Map<string, number>>();
    for (const p of sent) {
      const company = p.company!;
      if (!companyDayCounts.has(company)) companyDayCounts.set(company, new Map());
      const dayCounts = companyDayCounts.get(company)!;
      const d = new Date(p.sentAt!);
      const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      const count = (dayCounts.get(monthDay) ?? 0) + 1;
      dayCounts.set(monthDay, count);
      const label = count === 1 ? `${monthDay} Batch` : `${monthDay} Batch ${count}`;
      map.set(p.id, p.candidatesCount > 0 ? label : null);
    }
    return map;
  }, [packets]);

  const statusOptions = React.useMemo(() => Array.from(new Set(packets.map((p) => p.status))).sort(), [packets]);
  const companyOptions = React.useMemo(() => Array.from(new Set(packets.map((p) => p.company).filter(Boolean) as string[])).sort(), [packets]);

  // Filter
  let filtered = packets.filter((p) => {
    if (statusFilter.length > 0 && !statusFilter.includes(p.status)) return false;
    if (companyFilter.length > 0 && !companyFilter.includes(p.company ?? '')) return false;
    if (search) {
      const q = search.toLowerCase();
      if (p.name.toLowerCase().includes(q) || (p.company ?? '').toLowerCase().includes(q)) return true;
      const cached = candidateCache.current.get(p.id);
      if (cached) return cached.some((c) => c.talentId.includes(q) || (c.name ?? '').toLowerCase().includes(q));
      return false;
    }
    return true;
  });

  // Sort
  if (sort) {
    const { col, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    filtered = [...filtered].sort((a, b) => {
      let va: string | number = '', vb: string | number = '';
      if (col === 'name') { va = a.name.toLowerCase(); vb = b.name.toLowerCase(); }
      else if (col === 'company') { va = (a.company ?? '').toLowerCase(); vb = (b.company ?? '').toLowerCase(); }
      else if (col === 'candidates') { va = a.candidatesCount; vb = b.candidatesCount; }
      else if (col === 'status') { va = a.status; vb = b.status; }
      else if (col === 'created') { va = a.createdAt; vb = b.createdAt; }
      else if (col === 'sent') { va = a.sentAt ?? ''; vb = b.sentAt ?? ''; }
      else if (col === 'opened') { va = a.openedAt ?? ''; vb = b.openedAt ?? ''; }
      if (va < vb) return -1 * mul;
      if (va > vb) return 1 * mul;
      return 0;
    });
  }

  const handleSort = (col: string) => {
    setSort((prev) => prev?.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    setPage(1);
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const statusBadge = (status: string) => {
    if (status === 'sent') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Sent</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">Draft</span>;
  };

  if (selectedBatchId !== null) {
    return (
      <BatchDetailPage
        packetId={selectedBatchId}
        onBack={() => setSelectedBatchId(null)}
        onRemoveCandidate={handleRemoveFromDetail}
      />
    );
  }

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6 transition-opacity duration-300">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Batches</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Manage and send candidate batches to companies.</p>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
        {/* Search */}
        <div className="p-4 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-800">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by candidate name or ID..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
            />
          </div>
        </div>

        {/* Active filter chips */}
        {(() => {
          const hasFilters = statusFilter.length > 0 || companyFilter.length > 0;
          if (!hasFilters && !sort && !search) return null;
          return (
            <div className="px-4 pb-3 pt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Active filters:</span>
              {search && (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-100 dark:border-purple-800">
                  <span className="font-medium">Search:</span><span>{search}</span>
                  <button onClick={() => { setSearch(''); setPage(1); }} className="p-0.5 hover:bg-purple-100 dark:hover:bg-purple-800 rounded"><X className="w-3 h-3" /></button>
                </div>
              )}
              {sort && (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-100 dark:border-green-800">
                  <span className="font-medium">Sort:</span><span>{{ name: 'Name', company: 'Company', candidates: 'Candidates', status: 'Status', created: 'Created', sent: 'Sent', opened: 'Opened' }[sort.col] || sort.col} ({sort.dir})</span>
                  <button onClick={() => { setSort(null); setPage(1); }} className="p-0.5 hover:bg-green-100 dark:hover:bg-green-800 rounded"><X className="w-3 h-3" /></button>
                </div>
              )}
              {statusFilter.length > 0 && (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
                  <span className="font-medium">Status:</span><span>{statusFilter.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}</span>
                  <button onClick={() => { setStatusFilter([]); setPage(1); }} className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded"><X className="w-3 h-3" /></button>
                </div>
              )}
              {companyFilter.length > 0 && (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
                  <span className="font-medium">Company:</span><span>{companyFilter.join(', ')}</span>
                  <button onClick={() => { setCompanyFilter([]); setPage(1); }} className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded"><X className="w-3 h-3" /></button>
                </div>
              )}
              <button onClick={() => { setSearch(''); setSort(null); setStatusFilter([]); setCompanyFilter([]); setPage(1); }} className="text-xs text-gray-500 hover:text-gray-700 underline ml-2">Clear all</button>
            </div>
          );
        })()}

        {loading && <div className="flex flex-col items-center justify-center py-20"><Loader2 className="w-10 h-10 text-blue-500 animate-spin" /><span className="text-sm text-gray-500 dark:text-gray-400 mt-4">Loading batches...</span></div>}

        {!loading && filtered.length === 0 && (
          <div className="p-6 text-center py-12">
            <Package className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">No batches yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
              Create batches from the Candidates tab by selecting candidates and clicking "Add to Batch".
            </p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-600 dark:text-gray-400">
              <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <SortHeader label="Name" sortKey="name" currentSort={sort} onSort={handleSort} />
                  <SortHeader label="Company" sortKey="company" currentSort={sort} onSort={handleSort} filterOptions={companyOptions} filterValue={companyFilter} onFilter={(v) => { setCompanyFilter(v); setPage(1); }} />
                  <SortHeader label="Candidates" sortKey="candidates" currentSort={sort} onSort={handleSort} />
                  <SortHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} filterOptions={statusOptions} filterValue={statusFilter} onFilter={(v) => { setStatusFilter(v); setPage(1); }} />
                  <SortHeader label="Created" sortKey="created" currentSort={sort} onSort={handleSort} />
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Created by</th>
                  <SortHeader label="Sent" sortKey="sent" currentSort={sort} onSort={handleSort} />
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Sent by</th>
                  <SortHeader label="Opened" sortKey="opened" currentSort={sort} onSort={handleSort} />
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paged.map((packet) => (
                  <tr
                    key={packet.id}
                    onClick={() => { if (deletingId !== packet.id) setSelectedBatchId(packet.id); }}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-all ${deletingId === packet.id ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{packet.name}</div>
                      {renamingId === packet.id && (
                        <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Renaming...
                        </div>
                      )}
                      {duplicatingId === packet.id && (
                        <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Duplicating...
                        </div>
                      )}
                      {deletingId === packet.id && (
                        <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 mt-0.5">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Deleting...
                        </div>
                      )}
                      {copyingBatchId === packet.id && (
                        <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Adding {packet.candidatesCount} candidate{packet.candidatesCount !== 1 ? 's' : ''} to copy...
                        </div>
                      )}
                      {copyingBatchId !== packet.id && deletingId !== packet.id && duplicatingId !== packet.id && renamingId !== packet.id && customerNameMap.has(packet.id) && (customerNameMap.get(packet.id)
                        ? <div className="text-xs text-gray-400 mt-0.5">Shown as: {customerNameMap.get(packet.id)}</div>
                        : <div className="text-xs text-orange-400 mt-0.5">Not shown to customer (0 candidates)</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {packet.company ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">{packet.company}</span>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{packet.candidatesCount}</td>
                    <td className="px-4 py-3">{statusBadge(packet.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(packet.createdAt)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{packet.createdBy || <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{packet.sentAt ? formatDate(packet.sentAt) : <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{packet.sentBy || <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{packet.openedAt ? formatDate(packet.openedAt) : <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {packet.status === 'draft' && (
                          <button
                            onClick={() => setSendPacketTarget(packet)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Send className="w-3.5 h-3.5" />Send
                          </button>
                        )}
                        <BatchMenu
                          packet={packet}
                          onRename={() => setRenameTarget(packet)}
                          onDuplicate={() => handleDuplicate(packet)}
                          onDelete={() => setDeleteTarget(packet)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-900 dark:text-gray-100">{(page - 1) * PAGE_SIZE + 1}</span> to <span className="font-medium text-gray-900 dark:text-gray-100">{Math.min(page * PAGE_SIZE, filtered.length)}</span> of <span className="font-medium text-gray-900 dark:text-gray-100">{filtered.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(page - 1)} disabled={page <= 1} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <ChevronUp className="w-5 h-5 -rotate-90" />
                </button>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">Page {page} of {totalPages}</div>
                <button onClick={() => setPage(page + 1)} disabled={page >= totalPages} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <ChevronDown className="w-5 h-5 -rotate-90" />
                </button>
              </div>
            </div>
          )}
        </>
        )}
      </div>

      {/* Send Batch Modal */}
      {sendPacketTarget && (
        <SendBatchModal
          packet={sendPacketTarget}
          onClose={() => setSendPacketTarget(null)}
          onDone={() => {
            setSendPacketTarget(null);
            refreshPackets();
          }}
        />
      )}

      {/* Rename Modal */}
      {renameTarget && (
        <RenameModal
          packet={renameTarget}
          onClose={() => setRenameTarget(null)}
          onDone={async (newName) => {
            const targetId = renameTarget.id;
            setRenameTarget(null);
            setRenamingId(targetId);
            try {
              await renamePacket(targetId, newName);
              setPackets((prev) => prev.map((p) => p.id === targetId ? { ...p, name: newName } : p));
            } catch { /* silent */ }
            finally { setRenamingId(null); }
          }}
        />
      )}

      {/* Delete Batch Modal */}
      {deleteTarget && (
        <DeleteBatchModal
          packet={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirmed}
          deleting={false}
        />
      )}
    </div>
  );
}
